import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Users,
  Trash2,
  Edit3,
  Plus,
  Search,
  CheckCircle2,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/auth/hooks';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

interface ActionMeta {
  kind: string;
  op: 'delete' | 'update' | 'insert' | 'select';
  table: string;
  name?: string;
  phone?: string;
  sql?: string;
  user_text?: string;
  result_preview?: any;
  ts: string;
}

interface ChatHistory {
  id: string;
  business_id: string;
  user_id: string;
  role: string;
  content: string;
  meta?: ActionMeta;
  created_at: string;
}

const ACTION_ICONS: Record<string, any> = {
  create: Plus,
  insert: Plus,
  update: Edit3,
  delete: Trash2,
  select: Search,
};

const ACTION_COLORS: Record<string, string> = {
  create: 'from-green-500 to-emerald-500',
  insert: 'from-green-500 to-emerald-500',
  update: 'from-blue-500 to-cyan-500',
  delete: 'from-red-500 to-pink-500',
  select: 'from-purple-500 to-indigo-500',
};

const TABLE_ICONS: Record<string, any> = {
  customers: Users,
  appointments: Calendar,
  services: CheckCircle2,
  staff: Users,
  invoices: Clock,
};

export function ActionTimeline() {
  const { user } = useAuth();
  const businessId = user?.user_metadata?.business_id;

  const [actions, setActions] = useState<ChatHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'create' | 'update' | 'delete'>('all');

  useEffect(() => {
    loadActions();
  }, [businessId]);

  const loadActions = async () => {
    if (!businessId || !user?.id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('chat_histories')
        .select('*')
        .eq('business_id', businessId)
        .eq('user_id', user.id)
        .not('meta', 'is', null)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setActions((data as ChatHistory[]) || []);
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const filteredActions = actions.filter((action) => {
    if (filter === 'all') return true;
    return action.meta?.op === filter;
  });

  const getActionLabel = (action: ChatHistory) => {
    const meta = action.meta;
    if (!meta) return 'פעולה';

    if (meta.kind.includes('customer')) {
      if (meta.op === 'delete') return 'מחיקת לקוח';
      if (meta.op === 'insert') return 'יצירת לקוח';
      if (meta.op === 'update') return 'עדכון לקוח';
    }

    if (meta.kind.includes('appointment')) {
      if (meta.op === 'delete') return 'ביטול תור';
      if (meta.op === 'insert') return 'קביעת תור';
      if (meta.op === 'update') return 'עדכון תור';
    }

    if (meta.kind.includes('service')) {
      if (meta.op === 'delete') return 'מחיקת שירות';
      if (meta.op === 'insert') return 'יצירת שירות';
      if (meta.op === 'update') return 'עדכון שירות';
    }

    return 'פעולה';
  };

  const getActionDetails = (action: ChatHistory) => {
    const meta = action.meta;
    if (!meta) return null;

    const details = [];
    if (meta.name) details.push(`שם: ${meta.name}`);
    if (meta.phone) details.push(`טלפון: ${meta.phone}`);
    if (meta.user_text) details.push(meta.user_text);

    return details.join(' • ');
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="p-6 bg-white border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">היסטוריית פעולות</h2>

        {/* Filters */}
        <div className="flex gap-2">
          {(['all', 'insert', 'update', 'delete'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f === 'all' && 'הכל'}
              {f === 'insert' && 'יצירה'}
              {f === 'update' && 'עדכון'}
              {f === 'delete' && 'מחיקה'}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500">טוען היסטוריה...</p>
            </div>
          </div>
        ) : filteredActions.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">אין פעולות להצגה</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {filteredActions.map((action, index) => {
                const meta = action.meta;
                if (!meta) return null;

                const Icon = ACTION_ICONS[meta.op] || Search;
                const TableIcon = TABLE_ICONS[meta.table] || CheckCircle2;
                const gradient = ACTION_COLORS[meta.op] || 'from-gray-500 to-gray-600';

                return (
                  <motion.div
                    key={action.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`p-3 bg-gradient-to-br ${gradient} text-white rounded-xl`}>
                        <Icon className="h-5 w-5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{getActionLabel(action)}</h3>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <TableIcon className="h-3 w-3" />
                            <span>{meta.table}</span>
                          </div>
                        </div>

                        {getActionDetails(action) && (
                          <p className="text-sm text-gray-600 mb-2">{getActionDetails(action)}</p>
                        )}

                        {action.content && (
                          <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-2 mb-2">
                            {action.content}
                          </p>
                        )}

                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>
                              {format(new Date(action.created_at), 'dd/MM/yyyy HH:mm', {
                                locale: he,
                              })}
                            </span>
                          </div>
                          {meta.result_preview && (
                            <span className="text-green-600 font-medium">✓ הושלם</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
